import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { XClear, ArrowUpRight, AlertErrorCircle, OverflowMenu, Copy } from '@/assets/icons'
import { InviteEmailStatus } from '../schemas'
import { buildInviteLink } from '../utils/build-invite-link'
import OrgAdminConfirmationDialog, { OrgAdminConfirmationDialogProps } from './OrgAdminConfirmationDialog'
import { useDeleteInviteMutation, useResendInviteMutation } from '../apis/org-admin.api'
import { GlobalToastType, kProduction, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem, type MenuItemProps } from '@/components/menu/MenuItem'

type InviteListItemMenuOption = Pick<MenuItemProps, 'label' | 'icon' | 'variant' | 'onSelect'>

type Props = {
  inviteId: string
  inviteeEmail: string
  refreshData: () => void
  /** Raise confirmation dialog above another open modal. Default: false. */
  stacked?: boolean
}

const InviteListItemMenu: React.FC<Props> = (props: Props) => {
  const { inviteId, inviteeEmail, refreshData, stacked = false } = props
  const dispatch = useAppDispatch()

  // Dialog state
  const [openConfirmationDialogProps, setOpenConfirmationDialogProps] = useState<OrgAdminConfirmationDialogProps | null>(null)

  // RTK-Query Mutation
  const [deleteInvite] = useDeleteInviteMutation()
  const [resendInvite] = useResendInviteMutation()

  // Menu elements
  const menuElements: InviteListItemMenuOption[] = [
    {
      label: 'Resend invitation',
      icon: ArrowUpRight,
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Resend invitation',
          message: `Are you sure you want to resend ${inviteeEmail}'s invitation?`,
          visible: true,
          onConfirm: async () => {
            // Resend the invite
            const response = await resendInvite(inviteId).unwrap() // unwrap to allow popup to catch errors

            // Introduce an artificial delay
            await new Promise((resolve) => setTimeout(resolve, 1000))

            // Refresh the data
            refreshData()

            // Same feedback as invite creation: say so when the email was filtered
            // to the non-prod test inbox instead of the invitee (ENG-6983)
            if (response.email_status === InviteEmailStatus.REDIRECTED_TEST_INBOX) {
              dispatch(
                openGlobalToast({
                  type: GlobalToastType.WARNING,
                  message: `No email was sent to ${inviteeEmail} — this environment only emails @paxton.ai or allowlisted addresses. Use "Copy invite link" instead.`,
                  durationMs: kToastDurationMediumMs,
                })
              )
              return
            }
            if (response.email_status !== InviteEmailStatus.SENT) {
              // Unknown/future status - stay neutral rather than claiming delivery
              dispatch(
                openGlobalToast({
                  type: GlobalToastType.WARNING,
                  message: `Invitation resent, but delivery could not be confirmed. Use "Copy invite link" if it doesn't arrive.`,
                  durationMs: kToastDurationMediumMs,
                })
              )
              return
            }

            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Invitation resent`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
          stacked,
        })
      },
    },
    {
      label: 'Copy invite link',
      icon: Copy,
      onSelect: async () => {
        // Fallback delivery path: the accept link works regardless of whether the
        // invite email arrived (filtered off-prod, send failure, spam) - ENG-6983
        const inviteLink = buildInviteLink(window.location.origin, inviteId, inviteeEmail)
        await navigator.clipboard.writeText(inviteLink)
        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Invite link copied', durationMs: kToastDurationMediumMs }))
      },
    },
    {
      label: 'Revoke invitation',
      icon: XClear,
      variant: 'destructive',
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Revoke invitation',
          message: `Are you sure you want to revoke ${inviteeEmail}'s invitation?`,
          visible: true,
          destructive: true,
          onConfirm: async () => {
            await deleteInvite(inviteId).unwrap()
            await new Promise((resolve) => setTimeout(resolve, 1000))
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Invitation revoked`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
          stacked,
        })
      },
    },
  ]

  // Dev menu items
  if (!kProduction) {
    menuElements.push({
      label: 'Dev: Log invite',
      icon: AlertErrorCircle,
      onSelect: () => {
        console.log('invite', { inviteId, inviteeEmail })
      },
    })
  }

  return (
    <>
      {openConfirmationDialogProps && <OrgAdminConfirmationDialog {...openConfirmationDialogProps} />}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className="p-1 rounded-full text-brand-neutral-700 hocus:bg-brand-neutral-200 focus:outline-hidden data-[state=open]:bg-brand-neutral-200"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <OverflowMenu className="w-5 h-5" />
          </button>
        </DropdownMenu.Trigger>

        <MenuContent className="min-w-[200px]" align="end" collisionPadding={8}>
          <MenuItemGroup>
            {menuElements.map((item) => (
              <MenuItem key={item.label} label={item.label} icon={item.icon} variant={item.variant} onSelect={item.onSelect} />
            ))}
          </MenuItemGroup>
        </MenuContent>
      </DropdownMenu.Root>
    </>
  )
}

export default InviteListItemMenu
